import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import DatePicker, { registerLocale } from "react-datepicker";
import { ro } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import "./AddEmployees.css";

// Înregistrăm localizarea pentru react-datepicker
registerLocale("ro", ro);

const formatLocalDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const AddEmployees = ({ open, onClose }) => {
  // Date inițiale pentru formular, memorate pentru performanță cu useMemo
  const initialFormData = useMemo(
    () => ({
      first_name: "",
      last_name: "",
      department: "",
      role: "",
      contract_date: formatLocalDate(new Date()),
      phone: "",
      email: "",
      adress: "",
      type: "",
      status: "active",
    }),
    []
  );

  // State-uri principale pentru gestionarea formularului
  const [formData, setFormData] = useState(initialFormData);
  const [departmentsOptions, setDepartmentsOptions] = useState([]); // opțiuni pentru react-select
  const [contractTypesOptions, setContractTypesOptions] = useState([]); // opțiuni pentru react-select
  const [loading, setLoading] = useState(false); // loading pentru buton
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [error, setError] = useState(""); // mesaj eroare global
  const [success, setSuccess] = useState(""); // mesaj succes global
  const [showToast, setShowToast] = useState(false); // toast de succes
  const [fieldErrors, setFieldErrors] = useState({}); // erori pe câmpuri individuale

  // Gestionare upload avatar
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // referințe pentru input ascuns și abort controller
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef();

  // opțiuni pentru dropdown "status"
  const statusTypes = useMemo(
    () => [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
      { value: "suspended", label: "Suspended" },
    ],
    []
  );

  // Funcție care preia departamentele din backend
  const fetchDepartments = useCallback(async () => {
    try {
      setDepartmentsLoading(true);

      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      const res = await axiosInstance.get("/departments/", {
        signal: abortControllerRef.current.signal,
      });

      // Transformă pentru react-select
      const options = res.data.map((d) => ({
        value: d.id,
        label: d.name,
      }));
      setDepartmentsOptions(options);
    } catch (err) {
      if (err.name !== "AbortError" && err.code !== "ERR_CANCELED") {
        console.error(err);
        setError("Failed to load departments");
      }
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  // Când se deschide modalul, resetăm formularul și încărcăm datele necesare
  useEffect(() => {
    if (open) {
      setFormData(initialFormData);
      setError("");
      setSuccess("");
      setAvatarFile(null);
      setAvatarPreview(null);
      setFieldErrors({});
      fetchDepartments();

      // Fetch pentru tipuri de contract
      const fetchContractTypes = async () => {
        try {
          const res = await axiosInstance.get("/type-contract/");

          // Transformă pentru react-select
          const options = res.data.map((c) => ({
            value: c.id,
            label: c.name || c.type,
          }));
          setContractTypesOptions(options);
        } catch (err) {
          console.error("❌ Eroare la încărcarea tipurilor de contract:", err);
        }
      };

      fetchContractTypes();
    }

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [open, initialFormData, fetchDepartments]);

  // Curățăm preview-ul avatarului ca să evităm memory leak
  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // Gestionare modificare câmpuri de formular
  const handleChange = useCallback(
    (field) => (e) => {
      let value = e.target.value;

      // Curățare input telefon — doar cifre, max 15 caractere
      if (field === "phone") {
        value = value.replace(/\D/g, "");
        if (value.length > 15) {
          value = value.slice(0, 15);
        }
      }

      setFormData((prev) => ({ ...prev, [field]: value }));
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    },
    []
  );

  // Gestionare modificare select-uri cu react-select
  const handleSelectChange = useCallback(
    (field) => (selectedOption) => {
      const value = selectedOption ? selectedOption.value : "";
      setFormData((prev) => ({ ...prev, [field]: value }));
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    },
    []
  );

  const handleStatusChange = useCallback((selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({ ...prev, status: value }));
    setFieldErrors((prev) => ({ ...prev, status: "" }));
  }, []);

  // Gestionare modificare date
  const handleDateChange = useCallback((date) => {
    setFormData((prev) => ({
      ...prev,
      contract_date: formatLocalDate(date),
    }));
    setFieldErrors((prev) => ({ ...prev, contract_date: "" }));
  }, []);

  // Validare formular înainte de submit
  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.first_name.trim()) errors.first_name = "First name is required";
    if (!formData.last_name.trim()) errors.last_name = "Last name is required";
    if (!formData.department) errors.department = "Department is required";
    if (!formData.role.trim()) errors.role = "Role is required";
    if (!formData.contract_date) errors.contract_date = "Start date is required";
    if (!formData.phone.trim()) errors.phone = "Phone number is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.type) errors.type = "Contract type is required";
    if (!formData.status) errors.status = "Status is required";

    // Validare format email
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // Reset formular și închidere modal
  const handleCancel = useCallback(() => {
    setFormData(initialFormData);
    setError("");
    setSuccess("");
    setFieldErrors({});
    setAvatarFile(null);
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
    }
    setAvatarPreview(null);
    onClose(false);
  }, [initialFormData, onClose, avatarPreview]);

  // Trigger input pentru avatar
  const handleAvatarClick = useCallback(() => {
    if (!avatarUploading) fileInputRef.current?.click();
  }, [avatarUploading]);

  // Când utilizatorul selectează fișier avatar
  const handleAvatarChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Tipuri permise
      const allowed = ["image/jpeg", "image/png", "image/webp"];
      if (!allowed.includes(file.type)) {
        alert("Please select a valid image (JPG, PNG, WEBP).");
        e.target.value = "";
        return;
      }

      // Max 10MB
      const max = 10 * 1024 * 1024;
      if (file.size > max) {
        alert("Image too large (max 10MB).");
        e.target.value = "";
        return;
      }

      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }

      // Setăm fișier și preview
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    },
    [avatarPreview]
  );

  // Upload avatar pentru ID-ul nou creat
  const uploadAvatarForId = useCallback(
    async (employeeId) => {
      if (!avatarFile || !employeeId) return;

      setAvatarUploading(true);
      try {
        const fd = new FormData();
        fd.append("avatar", avatarFile);

        await axiosInstance.put(`/employees/${encodeURIComponent(employeeId)}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        console.log("✅ Avatar uploaded successfully for ID:", employeeId);
      } catch (err) {
        console.error("❌ Avatar upload failed:", err.response?.data || err);
      } finally {
        setAvatarUploading(false);
      }
    },
    [avatarFile]
  );

  // Submit formular + upload avatar opțional
  const handleSubmit = useCallback(async () => {
    setError("");
    setSuccess("");
    setFieldErrors({});

    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    try {
      const payload = {
        ...formData,
        department: formData.department ? parseInt(formData.department) : null,
        type: formData.type ? parseInt(formData.type) : null,
      };

      const res = await axiosInstance.post("/employees/", payload);

      if (res.status === 201) {
        // Upload avatar dacă s-a trimis unul
        if (avatarFile && res.data?.id) {
          await uploadAvatarForId(res.data.id);
        }

        setShowToast(true);
        setTimeout(() => setShowToast(false), 4000);

        onClose(true, "Employee added successfully!");
      } else {
        setError("Unexpected response from server");
      }
    } catch (err) {
      let message = "Failed to create employee";
      if (err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (typeof err.response?.data === "object" && err.response?.data !== null) {
        const firstKey = Object.keys(err.response.data)[0];
        const firstError = err.response.data[firstKey];
        if (Array.isArray(firstError)) {
          message = firstError[0];
        } else if (typeof firstError === "string") {
          message = firstError;
        }
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [formData, validateForm, onClose, avatarFile, uploadAvatarForId]);

  // Stiluri custom pentru react-select simplificate
  const getCustomSelectStyles = (fieldName) => ({
    control: (base, state) => ({
      ...base,
      border: fieldErrors[fieldName]
        ? "1px solid #d32f2f"
        : state.isFocused
        ? "1px solid #007BFF"
        : "1px solid #ccc",
      "&:hover": {
        border: fieldErrors[fieldName]
          ? "1px solid #d32f2f"
          : state.isFocused
          ? "1px solid #007BFF"
          : "1px solid #888",
      },
      fontSize: "14px",
      fontFamily: "Arial, sans-serif",
      minHeight: "38px",
      backgroundColor: "#fff",
      boxShadow: "none",
      borderRadius: "4px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      textAlign: "left",
    }),
    input: (base) => ({
      ...base,
      color: "#1a1a1a",
      margin: "0",
      padding: "0",
      "& input": {
        boxShadow: "none !important",
        border: "none !important",
        outline: "none !important",
        padding: "0",
        margin: "0",
        textAlign: "left",
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: "#999",
      fontSize: "14px",
      textAlign: "left",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    }),
    singleValue: (base) => ({
      ...base,
      color: "#1a1a1a",
      fontSize: "14px",
      textAlign: "left",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      fontSize: "14px",
      textAlign: "left",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#e6f2ff" : state.isFocused ? "#f0f0f0" : "#fff",
      color: state.isSelected ? "#006ce4" : "#1a1a1a",
      fontSize: "14px",
      textAlign: "left",
      "&:active": {
        backgroundColor: "#e6f2ff",
      },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "#666",
      "&:hover": {
        color: "#333",
      },
      padding: "4px 8px",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#666",
      "&:hover": {
        color: "#333",
      },
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "100%",
      display: "flex",
      alignItems: "center",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      marginTop: "8px",
      marginBottom: "8px",
    }),
  });

  if (!open) return null;

  return (
    <>
      {showToast && <div className="global-toast">✅ Employee added successfully!</div>}

      <div className="page-addemployee">
        <div className="add-employee-page">
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>Add Employee</h2>
                <button className="close-btn" onClick={handleCancel}>
                  ×
                </button>
              </div>

              <hr className="header-divider" />

              {loading && (
                <div className="loading-overlay">
                  <div className="loader"></div>
                  <span>Creating employee...</span>
                </div>
              )}

              {error && <div className="alert error">{error}</div>}
              {success && <div className="alert success">{success}</div>}

              <div className="form">
                <div className="avatar-picker-container">
                  <div className="avatar-circle">
                    {avatarPreview ? (
                      <img src={avatarPreview} alt="Avatar preview" />
                    ) : (
                      <div className="avatar-placeholder">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                    )}

                    {avatarUploading && (
                      <div className="avatar-overlay">
                        <div className="spinner-small"></div>
                      </div>
                    )}

                    <button
                      type="button"
                      className="avatar-plus-btn"
                      onClick={handleAvatarClick}
                      disabled={avatarUploading}
                    >
                      +
                    </button>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden-file-input"
                      onChange={handleAvatarChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="label-left">
                      Contract <span className="required">*</span>
                    </label>

                    <Select
                      name="type"
                      value={contractTypesOptions.find(
                        (option) => option.value === parseInt(formData.type)
                      )}
                      onChange={handleSelectChange("type")}
                      options={contractTypesOptions}
                      placeholder="Select contract type"
                      className={`multiselect-field ${fieldErrors.type ? "select-error" : ""}`}
                      classNamePrefix="select"
                      isSearchable={true}
                      isClearable={true}
                      styles={getCustomSelectStyles("type")}
                    />

                    {fieldErrors.type && (
                      <span className="field-error error-left">{fieldErrors.type}</span>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="label-left">
                      Status <span className="required">*</span>
                    </label>

                    <Select
                      name="status"
                      value={statusTypes.find((option) => option.value === formData.status)}
                      onChange={handleStatusChange}
                      options={statusTypes}
                      placeholder="Select status"
                      className={`multiselect-field ${fieldErrors.status ? "select-error" : ""}`}
                      classNamePrefix="select"
                      isSearchable={true}
                      isClearable={true}
                      styles={getCustomSelectStyles("status")}
                    />

                    {fieldErrors.status && (
                      <span className="field-error error-left">{fieldErrors.status}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="label-left">
                      First name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter first name"
                      value={formData.first_name}
                      onChange={handleChange("first_name")}
                      className={`input-left ${fieldErrors.first_name ? "field-error-border" : ""}`}
                      required
                    />
                    {fieldErrors.first_name && (
                      <span className="field-error error-left">{fieldErrors.first_name}</span>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="label-left">
                      Last name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter last name"
                      value={formData.last_name}
                      onChange={handleChange("last_name")}
                      className={`input-left ${fieldErrors.last_name ? "field-error-border" : ""}`}
                      required
                    />
                    {fieldErrors.last_name && (
                      <span className="field-error error-left">{fieldErrors.last_name}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="label-left">
                      Department <span className="required">*</span>
                    </label>

                    {departmentsLoading ? (
                      <div className="skeleton"></div>
                    ) : (
                      <Select
                        name="department"
                        value={departmentsOptions.find(
                          (option) => option.value === formData.department
                        )}
                        onChange={handleSelectChange("department")}
                        options={departmentsOptions}
                        placeholder="Select department"
                        className={`multiselect-field ${fieldErrors.department ? "select-error" : ""}`}
                        classNamePrefix="select"
                        isSearchable={true}
                        isClearable={true}
                        styles={getCustomSelectStyles("department")}
                      />
                    )}

                    {fieldErrors.department && (
                      <span className="field-error error-left">{fieldErrors.department}</span>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="label-left">
                      Role <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter role"
                      value={formData.role}
                      onChange={handleChange("role")}
                      className={`input-left ${fieldErrors.role ? "field-error-border" : ""}`}
                      required
                    />
                    {fieldErrors.role && (
                      <span className="field-error error-left">{fieldErrors.role}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="label-left">
                      Start Date <span className="required">*</span>
                    </label>

                    <div className="datepicker-container">
                      <DatePicker
                        selected={parseLocalDate(formData.contract_date)}
                        onChange={handleDateChange}
                        dateFormat="dd.MM.yyyy"
                        locale="ro"
                        placeholderText="dd.mm.yyyy"
                        className={`datepicker-input-with-icon ${
                          fieldErrors.contract_date ? "field-error-border" : ""
                        }`}
                        calendarClassName="custom-calendar"
                        popperPlacement="bottom-start"
                        required
                      />

                      <span
                        className="calendar-icon-inside"
                        onClick={() =>
                          document.querySelector(".datepicker-input-with-icon")?.focus()
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </span>
                    </div>

                    {fieldErrors.contract_date && (
                      <span className="field-error error-left">{fieldErrors.contract_date}</span>
                    )}
                  </div>

                  <div className="form-field phone-field">
                    <label className="label-left">
                      Phone number <span className="required">*</span>
                    </label>
                    <div className="input-with-icon">
                      <span className="phone-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" viewBox="0 0 24 24">
                          <path d="m14.662 23.038.012.007c2.46 1.566 5.71 1.21 7.792-.873l.774-.774a2.596 2.596 0 0 0 0-3.669l-3.26-3.26a2.596 2.596 0 0 0-3.67 0 1.093 1.093 0 0 1-1.546.002l-.001-.001-5.219-5.22a1.096 1.096 0 0 1 0-1.548 2.593 2.593 0 0 0 .002-3.666L6.284.77a2.596 2.596 0 0 0-3.669 0l-.774.774A6.285 6.285 0 0 0 .982 9.36L1 9.386a50.7 50.7 0 0 0 13.62 13.625z" />
                        </svg>
                      </span>

                      <input
                        type="text"
                        placeholder="Enter phone number"
                        value={formData.phone}
                        onChange={handleChange("phone")}
                        className={`input-left ${fieldErrors.phone ? "field-error-border" : ""}`}
                        required
                      />
                    </div>
                    {fieldErrors.phone && (
                      <span className="field-error error-left">{fieldErrors.phone}</span>
                    )}
                  </div>
                </div>

                <div className="form-field">
                  <label className="label-left">
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange("email")}
                    className={`input-left ${fieldErrors.email ? "field-error-border" : ""}`}
                    required
                  />
                  {fieldErrors.email && (
                    <span className="field-error error-left">{fieldErrors.email}</span>
                  )}
                </div>

                <div className="form-field">
                  <label className="label-left">Address</label>
                  <textarea
                    className="address-input input-left"
                    placeholder="Enter address"
                    value={formData.adress || ""}
                    onChange={handleChange("adress")}
                  />
                </div>

                <div className="form-buttons">
                  <button className="cancel-btn" onClick={handleCancel}>
                    Cancel
                  </button>

                  <button
                    className={`submit-btn ${loading ? "disabled" : ""}`}
                    onClick={!loading ? handleSubmit : undefined}
                    disabled={loading || departmentsLoading || avatarUploading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddEmployees;